// modules/freshcards/src/core/overlays/apply.ts
//
// 2026-09-18 (PROMPT F 20 Phase 2) — Apply / revert actions for FacePreference.
//
// applyOverlay(overlay, scope):
//   1. Register overlay in registry (idempotent)
//   2. Save FacePreference at scope (most-specific wins in cascade)
//   3. Clear resolver cache so next resolve() picks up the change
//   4. (Optional) call ThemeEngine.applyTheme on the resolved theme
//
// revertOverlay(scope):
//   1. Clear FacePreference at this scope
//   2. Clear resolver cache
//   3. Re-resolve — cascade falls back to parent scope or default
//
// Per operator constraint: "Show diff BEFORE applying" — `pendingDiff` from
// uiStore is the gate; applyOverlay() returns { ok: false, error: 'diff_required' }
// if pendingDiff is set without confirmation.

import type { AppOverlay } from '../../types/models/AppOverlay';
import type { Theme } from '../../integration/embedding/ThemeAPI';
import {
  loadFacePreference,
  saveFacePreference,
  clearFacePreference,
  type FacePreference,
  type FacePreferenceStorage,
  type FaceScope,
} from './facePreference';
import { getOverlayRegistry } from './registry';
import { clearResolverCache } from './resolver';
import { getUIState } from './uiStore';

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

export type ApplyResult =
  | {
      ok: true;
      /** Scope the preference was saved at. */
      scope: FaceScope;
      /** The overlay that ended up resolved (may differ from candidate if compat fallback). */
      applied: AppOverlay;
    }
  | {
      ok: false;
      error: 'diff_required' | 'validation_failed' | 'storage_failed' | 'unknown';
      detail?: string;
    };

export type RevertResult =
  | {
      ok: true;
      /** What the cascade fell back to after revert. */
      applied: AppOverlay;
    }
  | {
      ok: false;
      error: 'storage_failed' | 'unknown';
      detail?: string;
    };

// ---------------------------------------------------------------------------
// Apply
// ---------------------------------------------------------------------------

export interface ApplyOptions {
  /** Caller has shown the diff and confirmed. */
  diffConfirmed?: boolean;
  /** Optional theme applier (defaults to no-op; UI passes ThemeEngine.applyTheme). */
  applyTheme?: (theme: Theme) => void;
}

export async function applyOverlay(
  storage: FacePreferenceStorage,
  candidate: AppOverlay,
  scope: FaceScope,
  opts: ApplyOptions = {},
): Promise<ApplyResult> {
  // 1. Register (idempotent — registry.register errors if dup, so unregister first if present)
  const reg = getOverlayRegistry();
  if (reg.has(candidate.id) && !reg.get(candidate.id)) {
    // Sanity check — should not happen.
  }
  if (reg.has(candidate.id)) {
    const existing = reg.get(candidate.id);
    if (existing && existing.bundleHash !== candidate.bundleHash && !opts.diffConfirmed) {
      // Diff required — caller should call setPendingDiff and re-call with diffConfirmed=true.
      return { ok: false, error: 'diff_required', detail: 'overlay exists with different bundleHash; confirm diff first' };
    }
  } else {
    const r = reg.register(candidate);
    if (!r.ok) {
      return { ok: false, error: 'validation_failed', detail: r.error };
    }
  }

  // 2. Save FacePreference at scope
  const now = new Date().toISOString();
  const pref: FacePreference = {
    overlayId: candidate.id,
    setAt: now,
    updatedAt: now,
  };
  try {
    await saveFacePreference(storage, scope, pref);
  } catch (e) {
    return { ok: false, error: 'storage_failed', detail: String(e) };
  }

  // 3. Clear resolver cache so next resolve() picks up the change
  clearResolverCache();

  // 4. Re-resolve and apply theme
  const result = await reg.resolve(scope.userId, scope.projectId, scope.surfaceId, storage);
  if (!result.ok) {
    return { ok: false, error: 'unknown', detail: 'resolve failed' };
  }

  if (opts.applyTheme && result.resolved.overlay.theme) {
    try {
      opts.applyTheme(result.resolved.overlay.theme as Theme);
    } catch (e) {
      return { ok: false, error: 'unknown', detail: `applyTheme threw: ${String(e)}` };
    }
  }

  return {
    ok: true,
    scope,
    applied: result.resolved.overlay,
  };
}

// ---------------------------------------------------------------------------
// Revert
// ---------------------------------------------------------------------------

export interface RevertOptions {
  applyTheme?: (theme: Theme) => void;
}

export async function revertOverlay(
  storage: FacePreferenceStorage,
  scope: FaceScope,
  opts: RevertOptions = {},
): Promise<RevertResult> {
  // 1. Clear preference at this scope
  try {
    await clearFacePreference(storage, scope);
  } catch (e) {
    return { ok: false, error: 'storage_failed', detail: String(e) };
  }

  // 2. Clear resolver cache
  clearResolverCache();

  // 3. Re-resolve — cascade falls back to parent
  const reg = getOverlayRegistry();
  const result = await reg.resolve(scope.userId, scope.projectId, scope.surfaceId, storage);
  if (!result.ok) {
    return { ok: false, error: 'unknown', detail: 'resolve failed' };
  }

  if (opts.applyTheme && result.resolved.overlay.theme) {
    try {
      opts.applyTheme(result.resolved.overlay.theme as Theme);
    } catch (e) {
      return { ok: false, error: 'unknown', detail: `applyTheme threw: ${String(e)}` };
    }
  }

  return {
    ok: true,
    applied: result.resolved.overlay,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Find the currently-applied overlay for a scope. Used by the picker to show
 * the diff ("you are switching FROM <current> TO <candidate>").
 */
export async function findCurrentlyApplied(
  storage: FacePreferenceStorage,
  scope: FaceScope,
): Promise<{ pref: FacePreference; scope: FaceScope } | null> {
  // Walk the cascade to find what would resolve today.
  const reg = getOverlayRegistry();
  const result = await reg.resolve(scope.userId, scope.projectId, scope.surfaceId, storage);
  if (!result.ok || result.resolved.source === 'default') {
    return null;
  }
  // Re-find the pref directly to get the scope triple.
  const userId = scope.userId;
  if (userId !== undefined && result.resolved.source === 'account') {
    const pref = await loadFacePreference(storage, { userId });
    return pref ? { pref, scope: { userId } } : null;
  }
  if (result.resolved.source === 'project') {
    const pref = await loadFacePreference(storage, { userId, projectId: scope.projectId });
    return pref ? { pref, scope: { userId, projectId: scope.projectId } } : null;
  }
  if (result.resolved.source === 'surface') {
    const pref = await loadFacePreference(storage, { userId, projectId: scope.projectId, surfaceId: scope.surfaceId });
    return pref ? { pref, scope: { userId, projectId: scope.projectId, surfaceId: scope.surfaceId } } : null;
  }
  return null;
}

/**
 * Helper used by the React layer to gate apply with "show diff BEFORE applying".
 * If a pendingDiff is in uiStore and it doesn't match the candidate, return false.
 */
export function diffApproved(candidate: AppOverlay): boolean {
  const ui = getUIState();
  if (!ui.pendingDiff) return true;
  return ui.pendingDiff.candidate.id === candidate.id;
}
